/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IDictionary, IModuleContract, IPageResponse, IRenderingContext } from '@msdyn365-commerce/core-internal';
import { isLazyRenderModuleEnabled } from './helpers';

interface IPageResponseWithModules extends IPageResponse {
    modules: IDictionary<IModuleContract>;
}

function getFlattenedListOfChildren(
    context: IRenderingContext | null,
    slot: IModuleContract,
    fragmentId?: string,
    renderChildrenOnClient?: boolean
): IModuleContract[] {
    if (!slot) {
        return [];
    }

    let slotArr: IModuleContract[] = [slot];
    // tslint:disable-next-line:no-unused-expression
    slot.modules &&
        Object.values(slot.modules).forEach((current: IModuleContract[]) => {
            if (current) {
                current.forEach(m => {
                    if (fragmentId) {
                        m.id = `${fragmentId}__${m.id}`;
                    }

                    // if module entry points change is enabled and parent module has clientRender true
                    // set the config on all child modules as well
                    let _renderChildrenOnClient = renderChildrenOnClient;
                    if (context && isLazyRenderModuleEnabled(context)) {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        if (m.config && (<any>m.config).clientRender) {
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            _renderChildrenOnClient = (<any>m.config).clientRender;
                        }

                        if (_renderChildrenOnClient) {
                            m.config = m.config || {};
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            (<any>m.config).clientRender = _renderChildrenOnClient;
                        }
                    }

                    slotArr = slotArr.concat(getFlattenedListOfChildren(context, m, fragmentId, _renderChildrenOnClient));
                });
            }
        });
    return slotArr;
}

export function getFlattenedListOfModulesForFragment(fragment: IModuleContract, fragmentId: string): IModuleContract[] {
    if (!fragment) {
        return [];
    }
    return getFlattenedListOfChildren(null, fragment, fragmentId);
}

export function getFlattenedListOfModules(pageResponse: IPageResponse): IModuleContract[] {
    if (!pageResponse) {
        return [];
    }
    const response = <IPageResponseWithModules>pageResponse;
    const propValue = response.slots;
    if (propValue) {
        return propValue;
    }

    const modulesFromLegacy = response.modules && Object.values(response.modules);
    if (modulesFromLegacy && modulesFromLegacy.length) {
        return modulesFromLegacy.map((m: IModuleContract) => m);
    } else {
        return getFlattenedListOfChildren(pageResponse.renderingContext, pageResponse.pageRoot);
    }
}
